/**
 * Shared helpers for the roles and external-roles tabs.
 *
 * The permissions system is group-template + direct-assignment:
 *   - PermissionGroups are templates. Clicking a group chip in the UI
 *     unions its permissions into the role (apply-once action).
 *   - Role.permissions is the source of truth - the matrix reads and
 *     writes this set directly.
 *
 * This module provides the two cross-tab utilities both pages need:
 *   - groupsByEntity: bins groups under entity section headers for the
 *     chip picker.
 *   - buildMatrixLayout: derives the module × action grid from the full
 *     permission list so the matrix renders the same structure everywhere.
 */

export interface Permission {
  id: number
  codename: string
  name: string
  is_suitable_for_external_assignment: boolean
}

export interface PermissionGroup {
  id: number
  uuid: string
  name: string
  description: string
  permissions: Permission[]
  is_suitable_for_external_assignment: boolean
}

/**
 * The "floor" - codenames that can NEVER be granted to an external (partner)
 * role, regardless of any override. Escalation / cross-org / platform-integrity
 * primitives only. These rows stay locked in the external permission editor even
 * for an admin holding `external-roles.grant_unsafe`.
 *
 * MUST mirror `NEVER_EXTERNAL_ASSIGNABLE_CODENAMES` in
 * `backend/apps/organisations/external_role_overrides.py`. The backend is the
 * real boundary (serializer + m2m signal both reject these); this mirror only
 * drives the UI so a floor codename is never offered as selectable.
 */
export const EXTERNAL_ROLE_FLOOR_CODENAMES: ReadonlySet<string> = new Set([
  'users.view',
  'users.manage',
  'roles.view',
  'roles.manage',
  'api-keys.view',
  'api-keys.create',
  'api-keys.delete',
  'api-logs.view',
])

/** The dedicated permission an admin must hold to override a not-recommended
 * permission onto a partner role. Mirrors `GRANT_UNSAFE_CODENAME` on the backend. */
export const GRANT_UNSAFE_CODENAME = 'external-roles.grant_unsafe'

/**
 * How a permission behaves for the role being edited. Shared by the list and
 * matrix views of the permission editor:
 *  - 'grantable': freely selectable (recommended / external-safe, or any
 *    permission when not in external-safe mode).
 *  - 'override':  not recommended for a partner role, but an admin holding
 *    `external-roles.grant_unsafe` may grant it deliberately, per write, behind
 *    a confirmation.
 *  - 'locked':    can never be granted on this role - a floor codename, or a
 *    not-recommended codename the current actor lacks the capability to override.
 */
export type PermState = 'grantable' | 'override' | 'locked'

// ---------------------------------------------------------------------------
// Module / action labels and display order
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Permission categories for the sidebar permission editor
// ---------------------------------------------------------------------------

export const PERMISSION_CATEGORIES = [
  'Catalogue',
  'Operations',
  'Coverage',
  'Workflow and approvals',
  'Customers and comms',
  'Admin and system',
  'Developer',
  'Entity permissions',
] as const

export type PermissionCategory = (typeof PERMISSION_CATEGORIES)[number]

export const MODULE_CATEGORIES: Record<string, PermissionCategory> = {
  products: 'Catalogue',
  'product-categories': 'Catalogue',
  'product-templates': 'Catalogue',
  'product-brands': 'Catalogue',

  holdings: 'Operations',
  claims: 'Operations',
  'claim-schemas': 'Operations',
  'claim-workflows': 'Operations',
  inspections: 'Operations',
  contractors: 'Operations',
  suppliers: 'Operations',

  warranties: 'Coverage',
  'warranty-templates': 'Coverage',
  'warranty-mappings': 'Coverage',
  'warranty-options': 'Coverage',
  'proration-formulas': 'Coverage',
  pricing: 'Coverage',
  'labor-rates': 'Coverage',

  transfers: 'Workflow and approvals',
  workflows: 'Workflow and approvals',
  exceptions: 'Workflow and approvals',
  loa: 'Workflow and approvals',

  customers: 'Customers and comms',
  'email-triggers': 'Customers and comms',
  reminders: 'Customers and comms',
  templates: 'Customers and comms',
  hoa: 'Customers and comms',

  users: 'Admin and system',
  roles: 'Admin and system',
  organisation: 'Admin and system',
  translations: 'Admin and system',
  attachments: 'Admin and system',
  'table-views': 'Admin and system',
  partnerships: 'Admin and system',
  impersonation: 'Admin and system',
  reports: 'Admin and system',
  'geo-zones': 'Admin and system',
  plants: 'Admin and system',
  uom: 'Admin and system',

  'api-keys': 'Developer',
  'api-logs': 'Developer',
  webhooks: 'Developer',
}

export interface PermissionModule {
  key: string
  label: string
  permissions: Permission[]
}

export interface PermissionCategoryGroup {
  category: PermissionCategory
  modules: PermissionModule[]
}

/**
 * Group all permissions by category and module for the sidebar editor.
 * Modules not in MODULE_CATEGORIES are placed under "Entity permissions".
 */
export function groupPermissionsByCategory(
  permissions: Permission[],
): PermissionCategoryGroup[] {
  const byModule = new Map<string, Permission[]>()
  for (const p of permissions) {
    const dot = p.codename.indexOf('.')
    if (dot === -1) continue
    const mod = p.codename.substring(0, dot)
    const action = p.codename.substring(dot + 1)
    if (LEGACY_REGISTERED_ENTITY_MODULES.has(mod) && LEGACY_CRUD_ACTIONS.has(action)) continue
    if (!byModule.has(mod)) byModule.set(mod, [])
    byModule.get(mod)!.push(p)
  }

  const byCat = new Map<PermissionCategory, PermissionModule[]>()
  for (const cat of PERMISSION_CATEGORIES) {
    byCat.set(cat, [])
  }

  for (const [mod, perms] of byModule) {
    const cat = MODULE_CATEGORIES[mod] ?? 'Entity permissions'
    const label =
      MODULE_LABELS[mod] ?? humaniseModule(mod)
    byCat.get(cat as PermissionCategory)?.push({ key: mod, label, permissions: perms })
  }

  const result: PermissionCategoryGroup[] = []
  for (const cat of PERMISSION_CATEGORIES) {
    const modules = byCat.get(cat) ?? []
    if (modules.length === 0) continue
    modules.sort((a, b) => a.label.localeCompare(b.label))
    result.push({ category: cat, modules })
  }
  return result
}

// ---------------------------------------------------------------------------
// Module / action labels and display order
// ---------------------------------------------------------------------------

/** Display order for modules in the matrix rows. */
export const MODULE_ORDER = [
  'products',
  'product-categories',
  'product-templates',
  'holdings',
  'transfers',
  'claims',
  'claim-schemas',
  'claim-workflows',
  'customers',
  'warranty-templates',
  'proration-formulas',
  'warranties',
  'translations',
  'users',
  'roles',
  'organisation',
  'table-views',
  'exceptions',
] as const

/** Human-readable labels for each module, used as matrix row headers. */
export const MODULE_LABELS: Record<string, string> = {
  products: 'Products',
  'product-categories': 'Product Categories',
  'product-templates': 'Product Templates',
  holdings: 'Registrations',
  // The transfers row only surfaces ``view``/``create`` in the CRUD
  // matrix. Lifecycle actions (approve/deny/hold/cancel/request_docs/
  // request_payment/upload_proof/waive_fee/override_window) are granted
  // independently and don't fit the CRUD column scheme - a dedicated
  // workflow-actions surface is tracked as a follow-up.
  transfers: 'Transfers',
  claims: 'Claims',
  'claim-schemas': 'Claim Schemas',
  'claim-workflows': 'Claim Workflows',
  customers: 'Customers',
  'warranty-templates': 'Guarantee Classes',
  'proration-formulas': 'Proration Formulas',
  warranties: 'Guarantees',
  translations: 'Translations',
  users: 'Users',
  roles: 'Roles',
  organisation: 'Organisation',
  'table-views': 'Table Views',
  exceptions: 'Exceptions',
  'geo-zones': 'Geographic Zones',
  impersonation: 'Impersonation',
  reports: 'Reports',
  contractors: 'Contractors',
  suppliers: 'Suppliers',
  pricing: 'Product Pricing',
  'labor-rates': 'Labor Rates',
  plants: 'Plants',
  loa: 'Limits of Authority',
  hoa: 'HOA',
  uom: 'Units of Measure',
}

/** Matrix columns.
 *
 * Each column maps to one or more permission action suffixes. Columns
 * exist to group semantically-equivalent actions across entities with
 * different shapes:
 *
 *   - ACL-shaped entities (products, holdings, claims) have
 *     ``view.global`` / ``view.acl`` and ``edit.global`` / ``edit.acl``.
 *   - Non-ACL entities have plain ``view`` and ``edit``.
 *
 * The plain variants are mutually exclusive with the ``.global`` /
 * ``.acl`` suffixes (a given entity has one shape or the other), so we
 * fold ``view.global`` + ``view`` into a single "View All" column and
 * ``edit.global`` + ``edit`` into "Edit All". No row ever has both.
 *
 * "Granted" is the label for ``.view.acl`` / ``.edit.acl`` - the user
 * sees/edits only the subset of rows that have been explicitly granted
 * to them via the ACL system (either by admin assignment for products,
 * or by partnership membership for holdings/claims). It's not "Own"
 * because the user didn't necessarily create those rows - they were
 * granted access.
 */
export interface MatrixColumnDef {
  label: string
  /** Permission action suffixes that populate this column. First match wins per row. */
  keys: readonly string[]
}

export const MATRIX_COLUMNS: readonly MatrixColumnDef[] = [
  { label: 'View All', keys: ['view.global', 'view'] },
  { label: 'View Granted', keys: ['view.acl'] },
  { label: 'Create', keys: ['create'] },
  { label: 'Edit All', keys: ['edit.global', 'edit'] },
  { label: 'Edit Granted', keys: ['edit.acl'] },
  { label: 'Delete', keys: ['delete'] },
  { label: 'Manage', keys: ['manage', 'manage-defaults'] },
  { label: 'Export', keys: ['export'] },
]

/** Flat list of every action key for convenience (filtering, key sets). */
export const ALL_ACTION_KEYS: readonly string[] = MATRIX_COLUMNS.flatMap(
  (c) => c.keys
)

const PERMISSION_NAME_PREFIXES = [
  'View all ',
  'View granted ',
  'View ',
  'Create ',
  'Edit all ',
  'Edit granted ',
  'Edit ',
  'Delete ',
  'Manage ',
  'Export ',
] as const

const LEGACY_CRUD_ACTIONS = new Set(['view', 'create', 'edit', 'delete'])

const LEGACY_REGISTERED_ENTITY_MODULES = new Set([
  'claim',
  'claim_schema',
  'consumer',
  'contractor',
  'holding',
  'host',
  'organisation',
  'product',
  'product_template',
  'tenant',
  'user',
  'warranty',
  'warranty_category',
  'warranty_option',
  'warranty_template',
])

// ---------------------------------------------------------------------------
// Template chip grouping
// ---------------------------------------------------------------------------

/**
 * Parses a group name like "Manage Own Registrations" or "View Products"
 * into its variant ("view" / "manage" / "view-own" / "manage-own") and
 * entity label. Used to render chips under entity section headers.
 */
export function parseGroupName(name: string): {
  variant: 'view' | 'manage' | 'view-own' | 'manage-own' | 'other'
  entity: string
} {
  // Check longer prefixes first
  if (name.startsWith('Manage Own ')) {
    return { variant: 'manage-own', entity: name.slice('Manage Own '.length) }
  }
  if (name.startsWith('View Own ')) {
    return { variant: 'view-own', entity: name.slice('View Own '.length) }
  }
  if (name.startsWith('Manage ')) {
    return { variant: 'manage', entity: name.slice('Manage '.length) }
  }
  if (name.startsWith('View ')) {
    return { variant: 'view', entity: name.slice('View '.length) }
  }
  return { variant: 'other', entity: name }
}

export interface EntityGroupSection {
  entity: string
  groups: Array<{
    group: PermissionGroup
    variant: 'view' | 'manage' | 'view-own' | 'manage-own' | 'other'
  }>
}

/**
 * Bin permission groups under entity section headers for the chip picker.
 * Ordering: follow MODULE_ORDER where we can match, then alphabetical.
 */
export function groupsByEntity(groups: PermissionGroup[]): EntityGroupSection[] {
  const byEntity = new Map<string, EntityGroupSection>()
  const otherSection: EntityGroupSection = { entity: 'Other', groups: [] }

  for (const g of groups) {
    const { variant, entity } = parseGroupName(g.name)
    if (variant === 'other') {
      otherSection.groups.push({ group: g, variant })
      continue
    }
    let section = byEntity.get(entity)
    if (!section) {
      section = { entity, groups: [] }
      byEntity.set(entity, section)
    }
    section.groups.push({ group: g, variant })
  }

  // Sort each section's chips by variant order: view → view-own → manage-own → manage
  const variantOrder: Record<string, number> = {
    view: 0,
    'view-own': 1,
    'manage-own': 2,
    manage: 3,
    other: 4,
  }
  for (const section of byEntity.values()) {
    section.groups.sort((a, b) => variantOrder[a.variant] - variantOrder[b.variant])
  }

  // Sort sections alphabetically by entity display name
  const sections = Array.from(byEntity.values()).sort((a, b) =>
    a.entity.localeCompare(b.entity)
  )
  if (otherSection.groups.length > 0) sections.push(otherSection)
  return sections
}

// ---------------------------------------------------------------------------
// Matrix layout
// ---------------------------------------------------------------------------

export interface MatrixCell {
  module: string
  /** The column this cell belongs to (label, e.g. "View All"). */
  columnLabel: string
  /** The actual permission codename this cell toggles. */
  codename: string
  isExternalSafe: boolean
}

export interface MatrixRow {
  module: string
  label: string
  /** Map from column label → cell. A row may be missing columns. */
  cellsByColumn: Map<string, MatrixCell>
}

/**
 * Derive the matrix layout from a flat permission list. For each module,
 * create a row with one cell per MATRIX_COLUMNS column that has a matching
 * permission for that module. Columns that can't be populated for a row
 * render as em-dash in the UI.
 */
export function buildMatrixLayout(permissions: Permission[]): {
  rows: MatrixRow[]
  activeColumnLabels: Set<string>
} {
  // Group permissions by module → action suffix → Permission
  const byModule = new Map<string, Map<string, Permission>>()
  for (const p of permissions) {
    const dot = p.codename.indexOf('.')
    if (dot === -1) continue
    const mod = p.codename.substring(0, dot)
    const action = p.codename.substring(dot + 1)
    if (!byModule.has(mod)) byModule.set(mod, new Map())
    byModule.get(mod)!.set(action, p)
  }

  const activeColumnLabels = new Set<string>()
  const rows: MatrixRow[] = []
  const orderedModules = [
    ...(MODULE_ORDER as readonly string[]).filter((mod) => byModule.has(mod)),
    ...Array.from(byModule.keys())
      .filter((mod) => !(MODULE_ORDER as readonly string[]).includes(mod))
      .filter((mod) => !LEGACY_REGISTERED_ENTITY_MODULES.has(mod))
      .sort((a, b) =>
        moduleLabel(a, byModule.get(a)!).localeCompare(
          moduleLabel(b, byModule.get(b)!)
        )
      ),
  ]

  for (const mod of orderedModules) {
    const actions = byModule.get(mod)
    if (!actions) continue

    const cellsByColumn = new Map<string, MatrixCell>()
    for (const column of MATRIX_COLUMNS) {
      // First matching action key wins - e.g. "View All" picks up
      // view.global if present, else plain view.
      for (const key of column.keys) {
        const perm = actions.get(key)
        if (!perm) continue
        cellsByColumn.set(column.label, {
          module: mod,
          columnLabel: column.label,
          codename: perm.codename,
          isExternalSafe: perm.is_suitable_for_external_assignment,
        })
        activeColumnLabels.add(column.label)
        break
      }
    }

    if (cellsByColumn.size > 0) {
      rows.push({
        module: mod,
        label: moduleLabel(mod, actions),
        cellsByColumn,
      })
    }
  }

  return { rows, activeColumnLabels }
}

function moduleLabel(module: string, actions: Map<string, Permission>): string {
  if (MODULE_LABELS[module]) return MODULE_LABELS[module]

  for (const permission of actions.values()) {
    const label = entityLabelFromPermissionName(permission.name)
    if (label) return label
  }

  return humaniseModule(module)
}

function entityLabelFromPermissionName(name: string): string | null {
  for (const prefix of PERMISSION_NAME_PREFIXES) {
    if (name.startsWith(prefix)) {
      const label = name.slice(prefix.length).trim()
      return label || null
    }
  }
  return null
}

function humaniseModule(module: string): string {
  return module
    .split(/[-_]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}
